import React, { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import App from './App.tsx';
import './i18n';
import './index.css';
import { installApiAuthInterceptor, setApiAuthInstance } from './apiAuth.ts';
import { isChunkLoadError, reloadOnceForChunkError } from './lazyWithRetry';


installApiAuthInterceptor();
setApiAuthInstance();

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  state = { hasError: false, error: undefined as Error | undefined };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('App error:', error, info);
    if (isChunkLoadError(error)) reloadOnceForChunkError();
  }
  render() {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          onRetry={() => {
            if (isChunkLoadError(this.state.error)) {
              window.location.reload();
              return;
            }
            this.setState({ hasError: false, error: undefined });
          }}
        />
      );
    }
    return this.props.children;
  }
}

function ErrorBoundaryFallback({ error, onRetry }: { error?: Error; onRetry: () => void }) {
  const { t } = useTranslation();
  const chunkError = isChunkLoadError(error);
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui', maxWidth: 600 }}>
      <h2>{t('common.somethingWentWrong')}</h2>
      <p style={{ color: '#64748b' }}>
        {chunkError
          ? t('common.pageStartingOrUpdated', {
              defaultValue: 'This page is still starting or was updated. Refresh to load it.',
            })
          : error?.message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}
      >
        {t('common.tryAgain')}
      </button>
    </div>
  );
}

function ChunkLoadFallback() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" aria-label={t('common.loading')} />
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <Suspense fallback={<ChunkLoadFallback />}>
        <App />
      </Suspense>
    </ErrorBoundary>
  </StrictMode>,
);
